/**
 * sl-xxev (B7): Source-Selector display-text — pure, unit-testable.
 *
 * Same-doc selections omit `docTitle` from the trigger label —
 * repeating the current document's title is redundant noise. Cross-
 * doc selections keep `docTitle / databaseTitle` so the user can see
 * which document owns the source.
 *
 * Extracted from `table-selector.tsx` so the logic can be exercised
 * without mounting the Lit/React dropdown.
 */

export interface SourceSelectorDisplayArgs {
  /** Webhook short-circuit: when present, the trigger shows the webhook name. */
  selectedWebhookName: string | null;
  /**
   * "Same as source" option label (already composed upstream). When
   * set, the trigger shows this label — takes precedence over a raw
   * database selection but not over a webhook.
   */
  sourceTableLabel: string | null;
  /** The resolved database row — `null` when nothing is selected. */
  selectedDatabase: { docTitle: string; databaseTitle: string } | null;
  /**
   * `true` when `selectedDatabase.docId !== currentDocId`. The caller
   * computes this because doc identity is not part of the row shape
   * consumed here.
   */
  isCrossDoc: boolean;
  /** Fallback shown when nothing is selected. */
  placeholder: string;
}

export function computeSourceSelectorDisplayText(
  args: SourceSelectorDisplayArgs
): string {
  if (args.selectedWebhookName) return args.selectedWebhookName;
  if (args.sourceTableLabel) return args.sourceTableLabel;
  if (!args.selectedDatabase) return args.placeholder;
  return args.isCrossDoc
    ? `${args.selectedDatabase.docTitle} / ${args.selectedDatabase.databaseTitle}`
    : args.selectedDatabase.databaseTitle;
}
